import React from "react";
import { useClerk, useUser } from "@clerk/react";
import { getClerkPublishableKey } from "../../cloud/clerkConfig";

const clerkPublishableKey = getClerkPublishableKey();

function AuthUserIcon({ className = "h-4 w-4" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="12" cy="7.5" r="4.5" />
      <path d="M4.5 21a7.5 7.5 0 0 1 15 0H4.5Z" />
    </svg>
  );
}

function AuthUserAvatar({ className = "h-4 w-4", imageUrl, name }) {
  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt=""
        className={`${className} rounded-full object-cover`}
        referrerPolicy="no-referrer"
        aria-hidden="true"
      />
    );
  }

  const initial = name?.trim()?.[0]?.toUpperCase() || "?";

  return (
    <span
      className={`${className} inline-flex items-center justify-center rounded-full bg-violet-600 text-[0.625rem] font-semibold leading-none text-white`}
      aria-hidden="true"
    >
      {initial}
    </span>
  );
}

function getUserDisplayName(user) {
  return (
    user?.fullName ||
    user?.username ||
    user?.primaryEmailAddress?.emailAddress ||
    user?.emailAddresses?.[0]?.emailAddress ||
    "Signed in"
  );
}

function getUserEmail(user) {
  return user?.primaryEmailAddress?.emailAddress || user?.emailAddresses?.[0]?.emailAddress || "";
}

function ClerkAuthMenuItem({ children, onCloseMenu }) {
  const { isLoaded, isSignedIn, user } = useUser();
  const { openSignIn, openUserProfile, signOut } = useClerk();
  const displayName = getUserDisplayName(user);
  const email = getUserEmail(user);

  const closeThenRun = React.useCallback(
    (action) => {
      onCloseMenu?.();
      window.setTimeout(() => {
        void action?.();
      }, 0);
    },
    [onCloseMenu]
  );

  const handleSignIn = React.useCallback(() => {
    if (!isLoaded) return;
    closeThenRun(openSignIn);
  }, [closeThenRun, isLoaded, openSignIn]);

  const handleManageAccount = React.useCallback(() => {
    if (!isLoaded) return;
    closeThenRun(openUserProfile);
  }, [closeThenRun, isLoaded, openUserProfile]);

  const handleSignOut = React.useCallback(() => {
    if (!isLoaded) return;
    closeThenRun(signOut);
  }, [closeThenRun, isLoaded, signOut]);

  const signedInIcon = (props) => (
    <AuthUserAvatar {...props} imageUrl={user?.imageUrl} name={displayName} />
  );

  return children({
    disabled: !isLoaded,
    email,
    icon: isSignedIn ? signedInIcon : AuthUserIcon,
    isSignedIn,
    label: isSignedIn ? displayName : "Sign in",
    onManageAccount: handleManageAccount,
    onSelect: isSignedIn ? undefined : handleSignIn,
    onSignOut: handleSignOut,
    profileIcon: signedInIcon,
  });
}

function ClerkAuthState({ children }) {
  const { isLoaded, isSignedIn } = useUser();
  const { openSignIn, openUserProfile } = useClerk();

  return children({
    isConfigured: true,
    isLoaded,
    isSignedIn,
    openSignIn,
    openUserProfile,
  });
}

function AuthState({ children }) {
  if (!clerkPublishableKey) {
    return children({
      isConfigured: false,
      isLoaded: true,
      isSignedIn: false,
      openSignIn: null,
      openUserProfile: null,
    });
  }
  return <ClerkAuthState>{children}</ClerkAuthState>;
}

function AuthMenuItem(props) {
  if (!clerkPublishableKey) {
    return props.children({
      disabled: true,
      email: "",
      icon: AuthUserIcon,
      isSignedIn: false,
      label: "Sign in unavailable",
      onManageAccount: undefined,
      onSelect: undefined,
      onSignOut: undefined,
      profileIcon: AuthUserIcon,
    });
  }
  return <ClerkAuthMenuItem {...props} />;
}

export { AuthMenuItem, AuthState, AuthUserIcon };
